import React, { useCallback, useEffect, useState } from 'react'
import { useParams, navigate } from '@reach/router'
import { useSelector, useDispatch } from 'react-redux'
import * as Modal from '_components/modal/generic-modal'
import { FormProvider, useForm } from 'react-hook-form'
import { Select } from '_/components/inputs'
import { NumericInput } from '_/views/budget-view-v2/components/Inputs/NumericInput'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useDialog } from '_/hooks/use-dialog'
import { useToast } from '_/hooks/use-toast'
import { Loader } from '@refera/ui-web'

import { getProvidersList } from '_modules/provider/actions'
import { getServiceOrder } from '_/modules/service-orders/actions'
import { currentServiceOrderSelector } from '_/modules/service-orders/selectors'
import { createAdditionalBudgets } from '_/modules/budget/actions'
import { formatErrorMessage } from '_/views/finance/utils/FormatErrorMessage'

import useStyles from './styles'

const schema = yup.object({
  provider1: yup
    .number()
    .required('Este campo é obrigatório')
    .typeError('Este campo é obrigatório'),
  provider2: yup
    .number()
    .nullable()
    .optional()
    .transform((value, originalValue) => {
      if (originalValue === '' || originalValue === null || originalValue === undefined) {
        return null
      }
      return value
    }),
  variation1: yup
    .number()
    .required('Este campo é obrigatório')
    .min(1, 'O valor mínimo é 1')
    .max(50, 'O valor máximo é 50'),
  variation2: yup
    .number()
    .nullable()
    .optional()
    .min(1, 'O valor mínimo é 1')
    .max(50, 'O valor máximo é 50'),
})

export function AdditionalBudgetsModal({ isOpen, onClose }) {
  const styles = useStyles()
  const dispatch = useDispatch()
  const { serviceOrderId, budgetId } = useParams()
  const serviceOrder = useSelector(currentServiceOrderSelector)
  const [providers, setProviders] = useState([])
  const [loading, setLoading] = useState(false)
  const city = serviceOrder.toJS()?.property?.city
  const { showDialog, closeDialog } = useDialog()
  const { showToast } = useToast()

  const methods = useForm({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {
      provider1: null,
      provider2: null,
      variation1: 12,
      variation2: 12,
    },
  })

  const {
    handleSubmit,
    formState: { errors, dirtyFields },
  } = methods

  const onSubmit = useCallback(
    data => {
      setLoading(true)
      dispatch(createAdditionalBudgets(serviceOrderId, budgetId, data))
        .then(() => {
          showToast({ type: 'success' })
          onClose()
          dispatch(getServiceOrder(serviceOrderId))
          navigate(`/chamado/${serviceOrderId}`)
        })
        .catch(error => {
          showToast({
            message: formatErrorMessage(error),
            type: 'error',
          })
        })
        .finally(() => setLoading(false))
    },
    [serviceOrderId, budgetId, dispatch, showToast, onClose]
  )

  const handleCancel = () => {
    if (Object.keys(dirtyFields).length > 0) {
      showDialog({
        type: 'warning',
        subject: 'Você tem certeza que deseja cancelar estas alterações?',
        labelApprove: 'Sim',
        labelCancel: 'Não',
        onApprove: () => {
          closeDialog()
          onClose()
        },
        onCancel: closeDialog,
      })
    } else {
      onClose()
    }
  }

  const handleGetProviders = useCallback(() => {
    const params = {
      service: 'Manutenção de Desocupação / Vistoria',
      cities: city?.toLowerCase(),
      contract_type: 'mixed,standard',
      provides_refera: true,
      contract_status: 'signed',
      stages: 'released',
    }
    setLoading(true)
    dispatch(getProvidersList(params))
      .then(res => {
        setProviders(res?.results || [])
      })
      .catch(() =>
        showToast({
          type: 'error',
          message: 'Erro ao carregar prestadores.',
        })
      )
      .finally(() => setLoading(false))
  }, [city, dispatch])

  useEffect(() => {
    if (isOpen) {
      handleGetProviders()
    }
  }, [isOpen, handleGetProviders])

  return (
    <Modal.Root
      open={isOpen}
      onClose={onClose}
      className={styles.root}
      disablePortal
      disableAutoFocus
    >
      <Modal.TitleModal title="Criar orçamentos adicionais" />

      <Modal.Content>
        <FormProvider {...methods}>
          <Loader hasBackdrop open={loading} label="Aguarde..." />
          <form id="additional-budgets-form" className={styles.form}>
            <Select
              name="provider1"
              label="Prestador 1"
              options={providers}
              getLabel={item => item.name}
              getValue={item => item.id}
              required
              error={!!errors?.provider1}
              containerClasses={styles.select}
            />

            <NumericInput
              label="Variaçao 1"
              name="variation1"
              decimalScale={0}
              placeholder="0"
              hasError={errors?.variation1?.message}
              required
            />

            <Select
              name="provider2"
              label="Prestador 2"
              options={providers}
              getLabel={item => item.name}
              getValue={item => item.id}
              containerClasses={styles.select}
            />

            <NumericInput
              label="Variaçao 2"
              name="variation2"
              decimalScale={0}
              placeholder="0"
              hasError={errors?.variation2?.message}
            />
          </form>
        </FormProvider>
      </Modal.Content>

      <Modal.Actions>
        <Modal.ButtonRed onClick={handleCancel}>Cancelar</Modal.ButtonRed>
        <Modal.ButtonFullBlue onClick={handleSubmit(onSubmit)}>Criar</Modal.ButtonFullBlue>
      </Modal.Actions>
    </Modal.Root>
  )
}
